import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { DashboardService, SelectBrandItem } from '../../../services/dashboard.service';
import { ToggleSwitchComponent } from '../../../shared/components/toggle-switch/toggle-switch.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { StorageService } from '../../../services/storage.service';
import { LocalStorageKeys } from '../../../shared/types/storage';

@Component({
  selector: 'app-brand-selection-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ToggleSwitchComponent, ButtonComponent],
  templateUrl: './brand-selection-modal.component.html',
  styleUrl: './brand-selection-modal.component.scss'
})
export class BrandSelectionModalComponent {
  private dialogRef = inject(MatDialogRef<BrandSelectionModalComponent>);
  private dashboardService = inject(DashboardService);
  private storageService = inject(StorageService);

  public searchTerm = signal('');
  public brandVisibility = signal<Record<string, boolean>>({});

  public allBrands = computed((): SelectBrandItem[] => this.dashboardService.myBrandOptions() || []);

  public filteredBrands = computed((): SelectBrandItem[]  => {
    const term = this.searchTerm().toLowerCase();
    const brands = this.allBrands();
    if (!term) return brands;
    return brands.filter(brand => brand.label.toLowerCase().includes(term));
  });

  public selectedCount = computed((): number => {
    return this.allBrands().filter(brand => this.isBrandVisible(brand.key)).length;
  });

  public isBrandVisible(key: string): boolean {
    const visibility = this.brandVisibility();
    return visibility[key] ?? true;
  }

  constructor() {
    const storedVisibility = this.storageService.local.getItem(LocalStorageKeys.MANAGED_BRANDS_SIGNALS_VISIBILITY) || {};
    const initialVisibility: Record<string, boolean> = {};

    this.allBrands().forEach(brand => {
      initialVisibility[brand.key] = storedVisibility[brand.key] ?? true;
    });

    this.brandVisibility.set(initialVisibility);
  }

  public onToggleBrand(brand: SelectBrandItem, isEnabled: boolean): void {
    this.brandVisibility.update(visibility => ({
      ...visibility,
      [brand.key]: isEnabled
    }));
  }

  public save(): void {
    const visibility = this.brandVisibility();
    this.storageService.local.setItem(LocalStorageKeys.MANAGED_BRANDS_SIGNALS_VISIBILITY, visibility);
    this.dialogRef.close(visibility);
  }

  public cancel(): void {
    this.dialogRef.close();
  }
}
